import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import api from '../lib/api'
import { useToast } from '@/hooks/use-toast'
import { formatPhoneDisplay } from '@/lib/utils'
import { onSocketEvent } from '../hooks/useWebSocket'
import { format, formatDistanceToNow } from 'date-fns'
import {
  MessageCircle,
  Search,
  ChevronLeft,
  Bot,
  User
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'

interface SmsConversation {
  id: string
  contact_phone: string
  status: string
  started_at: string
  last_activity_at: string
  last_message: string | null
  last_sender: string | null
  message_count: number
  contact_name: string | null
  metadata: Record<string, any> | null
}

interface SmsMessage {
  id: string
  sender_type: 'visitor' | 'bot'
  sender_id: string
  content: string
  delivery_status: string | null
  metadata: Record<string, any> | null
  timestamp: string
}

export default function SmsPage() {
  const [searchParams] = useSearchParams()
  const preselectedId = searchParams.get('id')
  const { toast } = useToast()

  const [conversations, setConversations] = useState<SmsConversation[]>([])
  const [selectedConversation, setSelectedConversation] = useState<SmsConversation | null>(null)
  const [messages, setMessages] = useState<SmsMessage[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    loadConversations()
  }, [])

  // Auto-select conversation from URL param
  useEffect(() => {
    if (preselectedId && conversations.length > 0) {
      const conv = conversations.find(c => c.id === preselectedId)
      if (conv) selectConversation(conv)
    }
  }, [preselectedId, conversations])

  // Real-time updates via Socket.IO
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>
    const cleanup = onSocketEvent('sms:message', () => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        loadConversations()
        // Refresh messages if viewing a conversation
        if (selectedConversation) {
          loadMessages(selectedConversation.id)
        }
      }, 500)
    })
    return () => {
      cleanup()
      clearTimeout(timer)
    }
  }, [selectedConversation])

  const loadConversations = async () => {
    try {
      const res = await api.get('/api/voice/sms/conversations?limit=100')
      setConversations(res.data.conversations || [])
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load SMS conversations.', variant: 'destructive' })
    } finally {
      setIsLoading(false)
    }
  }

  const loadMessages = async (conversationId: string) => {
    setMessagesLoading(true)
    try {
      const res = await api.get(`/api/voice/sms/conversations/${conversationId}/messages`)
      setMessages(res.data.messages || [])
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load messages.', variant: 'destructive' })
    } finally {
      setMessagesLoading(false)
    }
  }

  const selectConversation = (conv: SmsConversation) => {
    setSelectedConversation(conv)
    loadMessages(conv.id)
  }

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true
    const phone = conv.contact_phone || ''
    const message = conv.last_message || ''
    const q = searchQuery.toLowerCase()
    return phone.includes(q) || message.toLowerCase().includes(q)
  })

  return (
    <div className="flex h-[calc(100dvh-13rem-env(safe-area-inset-bottom))] sm:h-[calc(100dvh-12rem-env(safe-area-inset-bottom))] md:h-[calc(100vh-4rem)] overflow-hidden">
      {/* Conversation List */}
      <div className={`${selectedConversation ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-96 border-r`}>
        <div className="px-4 pt-5 pb-4 border-b border-border/50 bg-gradient-to-b from-card to-card/50 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-brand-royal/20 to-brand-periwinkle/15 border border-brand-royal/20 flex items-center justify-center shrink-0">
                <MessageCircle className="h-4 w-4 text-brand-royal" />
              </div>
              <div className="min-w-0">
                <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-muted-foreground leading-none mb-0.5">Texts</p>
                <h1 className="text-base font-heading font-bold tracking-tight truncate">SMS Conversations</h1>
              </div>
            </div>
            <Badge variant="secondary">{conversations.length}</Badge>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by phone or message..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <ScrollArea className="flex-1">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">Loading...</div>
          ) : filteredConversations.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No SMS conversations yet</p>
              <p className="text-xs mt-1">Texts will appear here when someone messages your AI number.</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredConversations.map(conv => (
                <button
                  key={conv.id}
                  onClick={() => selectConversation(conv)}
                  className={`w-full text-left p-4 hover:bg-muted/50 transition-colors ${
                    selectedConversation?.id === conv.id ? 'bg-muted' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">
                        {conv.contact_name || formatPhoneDisplay(conv.contact_phone)}
                      </p>
                      {conv.contact_name && (
                        <p className="text-[10px] text-muted-foreground">{formatPhoneDisplay(conv.contact_phone)}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {conv.last_sender === 'bot' && (
                          <span className="text-emerald-600">AI: </span>
                        )}
                        {conv.last_message || 'No messages'}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className="text-[10px] text-muted-foreground">
                        {formatDistanceToNow(new Date(conv.last_activity_at), { addSuffix: true })}
                      </span>
                      <Badge variant="outline" className="text-[10px] px-1.5">
                        {conv.message_count}
                      </Badge>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Message Thread */}
      <div className={`${selectedConversation ? 'flex' : 'hidden md:flex'} flex-col flex-1`}>
        {selectedConversation ? (
          <>
            {/* Thread Header */}
            <div className="p-4 border-b flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setSelectedConversation(null)}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div>
                <p className="font-semibold">
                  {selectedConversation.contact_name || formatPhoneDisplay(selectedConversation.contact_phone)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {selectedConversation.contact_name && formatPhoneDisplay(selectedConversation.contact_phone)}
                  {selectedConversation.contact_name && ' \u00b7 '}
                  {selectedConversation.message_count} messages
                  {' \u00b7 '}
                  Started {format(new Date(selectedConversation.started_at), 'MMM d, yyyy h:mm a')}
                </p>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              {messagesLoading ? (
                <div className="text-center text-muted-foreground py-8">Loading messages...</div>
              ) : (
                <div className="space-y-3 max-w-2xl mx-auto">
                  {messages.map(msg => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_type === 'bot' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                          msg.sender_type === 'bot'
                            ? 'bg-brand-indigo text-white rounded-br-md'
                            : 'bg-muted rounded-bl-md'
                        }`}
                      >
                        <div className="flex items-center gap-1.5 mb-0.5">
                          {msg.sender_type === 'bot' ? (
                            <Bot className="h-3 w-3 opacity-70" />
                          ) : (
                            <User className="h-3 w-3 opacity-70" />
                          )}
                          <span className="text-[10px] opacity-70 font-medium">
                            {msg.sender_type === 'bot' ? 'AI Assistant' : 'Contact'}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        <div className="flex items-center justify-end gap-1 mt-1">
                          <span className="text-[10px] opacity-50">
                            {format(new Date(msg.timestamp), 'h:mm a')}
                          </span>
                          {msg.delivery_status && msg.sender_type === 'bot' && (
                            <span className="text-[10px] opacity-50">
                              {msg.delivery_status === 'delivered' ? ' \u2713\u2713' :
                               msg.delivery_status === 'sent' ? ' \u2713' :
                               msg.delivery_status === 'failed' ? ' \u2717' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Select a conversation to view messages</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
